import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { displayAddress } from '../core/format.js';

const FILTER_KEYS = ['all', 'with_units', 'no_units', 'listed'];

export class PropertiesView extends Component {
  constructor() {
    super();
    // showArchived flips the backing endpoint to ?archived=1; the local
    // list is always wiped + refetched on toggle so a stale active list
    // can't bleed into the archive view (or vice versa).
    this.state = { properties: null, error: null, showArchived: false };
    // Filter / search kept off-state so input edits don't trigger a full
    // re-render (which would lose focus on the search field). Mirrors the
    // TenanciesView pattern.
    this.filters = { search: '', chip: 'all' };
  }

  template() {
    const list = this.state.properties;
    const archived = !!this.state.showArchived;
    const hasList = Array.isArray(list) && list.length > 0;
    return `
      <section class="stack">
        <div class="panel">
          <div class="panel-head panel-head--cta">
            <div class="panel-titles">
              <div class="panel-title-eyebrow">${esc(t('properties.eyebrow'))}</div>
              <h2 class="panel-title">${esc(t('properties.title'))} <span class="panel-title-acc">${esc(t('properties.title_acc'))}</span></h2>
            </div>
            <div class="panel-status">
              <a class="btn btn-primary panel-cta" href="#/properties/new">${icon('plus', { size: 'sm' })}<span>${esc(t('properties.new_long'))}</span></a>
            </div>
          </div>
          <div class="panel-body">
            <p class="panel-lede">${esc(t('properties.lede'))}</p>
            ${hasList ? this._statsHtml(list, archived) : ''}
          </div>
        </div>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${list === null
          ? `<div class="muted">${esc(t('properties.loading'))}</div>`
          : list.length === 0
            ? `<div class="empty">${esc(archived ? t('properties.empty_archived') : t('properties.empty'))}</div>`
            : `
              ${this._filterBarHtml(archived)}
              <div data-list-region>${this._listHtml(this._apply(list))}</div>
            `}
      </section>
    `;
  }

  // Compact v5 .stats strip. Zero values are muted; non-zero use the
  // accent color so the eye lands on what matters first.
  _statsHtml(list, archived) {
    const total = list.length;
    const units = list.reduce((acc, p) => acc + Number(p.unit_count ?? 0), 0);
    const listed = list.filter((p) => !!p.public_slug).length;
    const noUnits = list.filter((p) => Number(p.unit_count ?? 0) === 0).length;
    const stat = (k, v, opts = {}) => {
      const isZero = Number(v) === 0;
      const cls = ['stat'];
      if (opts.acc && !isZero) cls.push('acc');
      if (isZero) cls.push('is-zero');
      return `<div class="${cls.join(' ')}"><div class="stat-k">${esc(k)}</div><div class="stat-v">${esc(String(v))}</div></div>`;
    };
    if (archived) {
      return `<div class="stats">${stat(t('properties.stats.archived'), total, { acc: true })}</div>`;
    }
    return `
      <div class="stats">
        ${stat(t('properties.stats.total'), total, { acc: true })}
        ${stat(t('properties.stats.units'), units, { acc: true })}
        ${stat(t('properties.stats.listed'), listed)}
        ${stat(t('properties.stats.no_units'), noUnits)}
      </div>
    `;
  }

  _filterBarHtml(archived) {
    const active = this.filters.chip;
    const chip = (key, label, pressed) => `
      <button type="button" class="chip${pressed ? ' chip-on' : ''}" data-chip="${esc(key)}" aria-pressed="${pressed ? 'true' : 'false'}">
        <span>${esc(label)}</span>
      </button>`;
    // Archived view tab + filter chips share one row in the v5 layout —
    // they're all "what list am I looking at" decisions.
    const tabActive = chip('__active',   t('properties.tabs.active'),   !archived);
    const tabArchived = chip('__archived', t('properties.tabs.archived'), archived);
    return `
      <div class="properties-toolbar">
        <input class="input" type="search" data-filter="search" placeholder="${esc(t('properties.filters.search_placeholder'))}" value="${esc(this.filters.search)}" />
        <div class="row" style="flex-wrap:wrap; gap:6px;">
          ${tabActive}
          ${tabArchived}
          <span class="properties-toolbar-sep" aria-hidden="true"></span>
          ${archived ? '' : FILTER_KEYS.map((k) => chip(k, t('properties.filters.' + k), active === k)).join('')}
        </div>
      </div>
    `;
  }

  _apply(list) {
    const search = this.filters.search.trim().toLowerCase();
    const chip = this.filters.chip;
    return list.filter((p) => {
      if (chip === 'with_units' && Number(p.unit_count ?? 0) === 0) return false;
      if (chip === 'no_units' && Number(p.unit_count ?? 0) > 0) return false;
      if (chip === 'listed' && !p.public_slug) return false;
      if (!search) return true;
      const hay = [p.name, p.address_line1, p.address_line2, p.city, p.postal_code, p.country]
        .filter(Boolean).join(' ').toLowerCase();
      return hay.includes(search);
    });
  }

  _listHtml(filtered) {
    if (filtered.length === 0) {
      return `<div class="empty">${esc(t('properties.no_match'))}</div>`;
    }
    return `<div class="stack">${filtered.map((p, i) => this._itemHtml(p, i)).join('')}</div>`;
  }

  _itemHtml(p, idx) {
    const num = String(idx + 1).padStart(2, '0');
    // displayAddress() handles the multi-location case ("Budapest, 3 helyszín")
    // when the property's units span more than one street tuple; otherwise
    // it formats the cached main-unit address.
    const addrInline = esc(displayAddress(p));
    const unitCount = Number(p.unit_count ?? 0);
    const noUnits = unitCount === 0;
    const listed = !!p.public_slug;
    const archived = !!p.deleted_at;
    const unitsWord = esc(t(unitCount === 1 ? 'properties.row_meta.unit' : 'properties.row_meta.units') || 'units');
    let toneCls;
    let toneLabel;
    if (archived) {
      toneCls = 'tile-tone-muted';
      toneLabel = esc(t('properties.archived_badge'));
    } else if (noUnits) {
      toneCls = 'tile-tone-warning';
      toneLabel = esc(t('properties.no_units_badge_short') || t('properties.no_units_badge'));
    } else if (listed) {
      toneCls = 'tile-tone-success';
      toneLabel = esc(t('properties.listed_badge'));
    } else {
      toneCls = 'tile-tone-success';
      toneLabel = esc(t('properties.row_meta.active') || 'Active');
    }
    const title = esc(p.name || p.address_line1 || 'Property');
    const subtitle = addrInline || '';
    return `
      <a class="property-tile ${toneCls}" href="#/properties/${esc(p.id)}">
        <span class="property-tile-num mono">${num}</span>
        <div class="property-tile-body">
          <div class="property-tile-title">${title}</div>
          ${subtitle ? `<div class="property-tile-sub">${subtitle}</div>` : ''}
        </div>
        <div class="property-tile-meta">
          <span class="property-tile-units mono">${esc(String(unitCount))} ${unitsWord}</span>
          <span class="property-tile-status">${toneLabel}</span>
        </div>
        ${icon('chevron-right', { class: 'property-tile-chev icon-faint' })}
      </a>
    `;
  }

  mount() {
    if (this.state.properties === null) {
      this._load();
      return;
    }
    this._wireFilters();
  }

  _wireFilters() {
    const root = this.el;
    if (!root) return;
    const search = root.querySelector('[data-filter="search"]');
    if (search) search.addEventListener('input', (e) => {
      this.filters.search = e.target.value;
      this._renderList();
    });
    root.querySelectorAll('[data-chip]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.getAttribute('data-chip') || 'all';
        // Pseudo-chips switch the underlying view (active vs archived
        // list); the regular filter chips just narrow the current list.
        if (key === '__active') {
          if (!this.state.showArchived) return;
          this.filters = { search: '', chip: 'all' };
          this.setState({ showArchived: false, properties: null });
          this._load();
          return;
        }
        if (key === '__archived') {
          if (this.state.showArchived) return;
          this.filters = { search: '', chip: 'all' };
          this.setState({ showArchived: true, properties: null });
          this._load();
          return;
        }
        this.filters.chip = key;
        this._refreshChips();
        this._renderList();
      });
    });
  }

  _refreshChips() {
    const root = this.el;
    if (!root) return;
    root.querySelectorAll('[data-chip]').forEach((btn) => {
      const on = btn.getAttribute('data-chip') === this.filters.chip;
      btn.classList.toggle('chip-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  _renderList() {
    const region = this.el && this.el.querySelector('[data-list-region]');
    if (!region || !Array.isArray(this.state.properties)) return;
    region.innerHTML = this._listHtml(this._apply(this.state.properties));
  }

  async _load() {
    try {
      const path = this.state.showArchived ? '/properties?archived=1' : '/properties';
      const res = await api.get(path);
      this.setState({ properties: res.data || [] });
    } catch (err) {
      // Set properties to [] so the next mount() doesn't re-trigger _load()
      // and spin a render loop on persistent failures (e.g. API 404).
      this.setState({
        properties: [],
        error: err instanceof ApiError ? err.message : t('properties.errors.load'),
      });
    }
  }
}
